import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Loader2, FileText, Upload, Check, X, AlertTriangle } from "lucide-react";

export function ReceiveInvoiceDialog({
  open, onOpenChange, loading, confirming, progress,
  comparison, invoiceFile, invoicePreview,
  onFileSelect, onConfirm,
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!loading && !confirming) onOpenChange(o); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Recibir con factura o ticket
          </DialogTitle>
          <DialogDescription>
            Sube una foto del ticket o factura. La IA comparara los productos comprados con la lista.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground font-medium">Procesando factura con IA...</p>
            <p className="text-xs text-muted-foreground mt-1">Extrayendo productos y comparando con la lista</p>
          </div>
        )}

        {confirming && (
          <div className="py-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground font-medium">{progress || "Guardando..."}</p>
          </div>
        )}

        {!loading && !comparison && !confirming && (
          <div className="space-y-4 py-4">
            <div
              className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer hover:bg-muted/30 transition-colors"
              onClick={() => document.getElementById("invoice-file-input")?.click()}
              data-testid="invoice-upload-area"
            >
              <Upload className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm font-medium">Arrastra o haz clic para subir</p>
              <p className="text-xs text-muted-foreground mt-1">JPG, PNG, PDF o WEBP</p>
              <input
                id="invoice-file-input"
                type="file"
                accept="image/*,.pdf"
                className="hidden"
                onChange={onFileSelect}
                data-testid="invoice-file-input"
              />
            </div>
            {invoicePreview && (
              <div className="border rounded-xl p-3 flex items-center gap-3">
                <img src={invoicePreview} alt="Preview" className="w-16 h-16 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{invoiceFile?.name}</p>
                  <p className="text-xs text-muted-foreground">{(invoiceFile?.size / 1024).toFixed(0)} KB</p>
                </div>
              </div>
            )}
          </div>
        )}

        {comparison && !confirming && (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-emerald-50 dark:bg-emerald-950/20 rounded-xl p-3">
                <div className="text-2xl font-bold text-emerald-700">{comparison.matched?.length || 0}</div>
                <div className="text-xs text-emerald-700">Coincidentes</div>
              </div>
              <div className="bg-amber-50 dark:bg-amber-950/20 rounded-xl p-3">
                <div className="text-2xl font-bold text-amber-700">{comparison.partial?.length || 0}</div>
                <div className="text-xs text-amber-700">Parciales</div>
              </div>
              <div className="bg-red-50 dark:bg-red-950/20 rounded-xl p-3">
                <div className="text-2xl font-bold text-red-700">{comparison.missing?.length || 0}</div>
                <div className="text-xs text-red-700">No comprados</div>
              </div>
            </div>

            {comparison.matched?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold flex items-center gap-2 mb-2">
                  <Check className="w-4 h-4 text-emerald-500" /> Comprados completos
                </h4>
                <div className="space-y-1">
                  {comparison.matched.map((m, i) => (
                    <div key={`matched-${i}`} className="flex items-center gap-2 text-sm py-1 px-2 rounded-lg bg-emerald-50/50 dark:bg-emerald-950/10">
                      <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                      <span className="flex-1">{m.name}</span>
                      <Badge variant="secondary" className="text-[10px]">{m.quantity} {m.unit}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {comparison.partial?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" /> Comprados parcialmente
                </h4>
                <div className="space-y-1">
                  {comparison.partial.map((p, i) => (
                    <div key={`partial-${i}`} className="flex items-center gap-2 text-sm py-1 px-2 rounded-lg bg-amber-50/50 dark:bg-amber-950/10">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                      <span className="flex-1">{p.name}</span>
                      <span className="text-xs text-muted-foreground">{p.purchased} de {p.needed} {p.unit}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {comparison.missing?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold flex items-center gap-2 mb-2">
                  <X className="w-4 h-4 text-red-500" /> No comprados
                </h4>
                <div className="space-y-1">
                  {comparison.missing.map((m, i) => (
                    <div key={`missing-${i}`} className="flex items-center gap-2 text-sm py-1 px-2 rounded-lg bg-red-50/50 dark:bg-red-950/10">
                      <X className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                      <span className="flex-1">{m.name}</span>
                      <Badge variant="destructive" className="text-[10px]">{m.quantity} {m.unit}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading || confirming}>
            Cancelar
          </Button>
          {comparison && (
            <Button onClick={onConfirm} disabled={confirming || loading} data-testid="confirm-invoice-receive-btn">
              {confirming ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Confirmar recepcion
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
